/* eslint-disable react/no-unused-prop-types */
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import Typography from '@mui/material/Typography';
import {Trans, useTranslation} from 'react-i18next';
import {Virtuoso} from 'react-virtuoso';
import {connect} from 'react-redux';

import FindMoreResults from './find-more-results.jsx';
import ResultsListItem from '@explorer/components/items-panel/result-item';
import {
  LIST_ITEM_HEIGHT,
  LoadMoreButton,
} from '@explorer/components/items-panel/item-components.jsx';
import {MAX_VISIBLE_RESULTS, UI_MODES} from '@explorer/constants';
import {
  MESSAGES_KEYS,
  getValue as getMessageSeen,
  setValue as setMessageSeen,
} from '@explorer/util/local-messages';
import {
  addResultToOrder,
  addResultToView,
  removeResultFromOrder,
  removeResultFromView,
  selectMulti,
  updateStatus,
} from '@explorer/actions/results';
import {continueSearch} from '@explorer/actions/search';
import {getSelectedItemTypes} from '@explorer/selectors/item-types';
import {isCompareSubject} from '@explorer/util/results';
import {notify} from '@explorer/actions/notifications';
import {setActiveResultId} from '@explorer/actions/sidebar';
import {trackEvent} from '@explorer/analytics';

const ItemContainer = props => (
  <Box display="flex" alignItems="center" my={2} {...props} />
);

const ResultsList = ({notify, updateStatus, continueSearch, ...props}) => {
  const {t} = useTranslation();
  const listRef = useRef();
  const activeResultIdx = useMemo(
    () =>
      props.results.findIndex(result => props.viewResults.includes(result.id)),
    [props.results, props.viewResults]
  );

  useEffect(() => {
    // scroll to top if sort direction is updated
    if (listRef.current) {
      listRef.current.scrollToIndex(0);
    }
  }, [props.sortDirection]);

  const moreResultsMessage =
    props.results.length === 0
      ? props.onlySkySat
        ? t('resultsList.lookingForHighRes', 'Looking for high res imagery?')
        : t('resultsList.noResults', 'Sorry, no results found')
      : props.onlySkySat
        ? t('resultsList.moreHighRes', 'Looking for more high res imagery?')
        : t('resultsList.moreResults', 'Looking for more results?');

  const loadMoreResults = useCallback(() => {
    trackEvent('Load More Results button clicked');
    updateStatus(true);
    continueSearch();
  }, [continueSearch, updateStatus]);

  const Footer = () => {
    if (!props.resultsComplete) {
      return (
        <LoadMoreButton
          data-qe="load-more"
          disabled={props.resultsLoading}
          onClick={loadMoreResults}
        >
          {props.resultsLoading
            ? t('resultsList.loading', 'Loading...')
            : t('resultsList.loadMore', 'Load more')}
        </LoadMoreButton>
      );
    } else if (props.resultsComplete) {
      return (
        <ItemContainer height="100%">
          <FindMoreResults>{moreResultsMessage}</FindMoreResults>
        </ItemContainer>
      );
    }
  };

  const onResultClick = (event, result) => {
    event.stopPropagation();
    const addModifier = event.metaKey || event.ctrlKey;
    const rangeModifer = event.shiftKey;

    const selectedResultsLength = props.viewResults.length;
    if (rangeModifer && selectedResultsLength > 0) {
      props.selectMulti({result});
      trackEvent('Shift-Click on Results list', {
        shiftClick: true,
      });
    } else {
      if (addModifier && props.viewResults.includes(result.id)) {
        props.removeResultFromView({resultId: result.id});
        props.removeResultFromOrder({resultId: result.id});
      } else {
        props.addResultToView({reset: !addModifier, resultId: result.id});

        // don't even bother if result doesn't have anything available
        if (result.properties.availableItems > 0) {
          props.addResultToOrder({reset: !addModifier, resultId: result.id});
        }
      }
    }
  };

  useEffect(() => {
    const selectedResultsLength = props.viewResults.length;
    const messageSeen = getMessageSeen(MESSAGES_KEYS.BULK_SELECTIONS);
    if (!messageSeen && selectedResultsLength > MAX_VISIBLE_RESULTS) {
      const message = (
        <div>
          <Typography variant="inherit" display="block">
            {t('resultsList.warningPartialMapSelections')}
          </Typography>
          <Typography variant="inherit" paragraph>
            <Trans i18nKey="resultsList.suggestionPartialResults" />
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                onChange={() => {
                  setMessageSeen(MESSAGES_KEYS.BULK_SELECTIONS, !messageSeen);
                }}
              />
            }
            label={t('resultsList.labelDismissPartial')}
          />
        </div>
      );
      const notification = {
        title: t('resultsList.notifyPartial'),
        message,
        persist: true,
        key: 'bulkOrderMessage',
        type: 'miniDialog',
      };

      notify(notification);

      trackEvent('User selected too many results to display', {
        resultCount: selectedResultsLength,
        maxCount: MAX_VISIBLE_RESULTS,
      });
    }
  }, [props.viewResults, notify, t]);

  const setActiveResult = result => {
    props.setActiveResultId(result.id);
  };

  const itemRenderer = index => {
    const result = props.results[index];

    const selected = props.isComparing
      ? isCompareSubject(props.subjects, result.id)
      : props.viewResults.includes(result.id);

    return (
      <ResultsListItem
        key={result.id}
        result={result}
        selected={selected}
        geometry={props.geometry}
        setActiveResult={setActiveResult}
        onClick={onResultClick}
        isComparing={props.isComparing}
        onRequestAccess={props.onRequestAccess}
      />
    );
  };

  return (
    <Virtuoso
      ref={listRef}
      overscan={500}
      totalCount={props.results.length}
      fixedItemHeight={LIST_ITEM_HEIGHT}
      initialTopMostItemIndex={activeResultIdx >= 0 ? activeResultIdx : 0}
      itemContent={itemRenderer}
      components={{
        Footer,
      }}
    />
  );
};

ResultsList.propTypes = {
  addResultToOrder: PropTypes.func,
  addResultToView: PropTypes.func,
  continueSearch: PropTypes.func,
  geometry: PropTypes.object,
  isComparing: PropTypes.bool,
  notify: PropTypes.func,
  onRequestAccess: PropTypes.func,
  onlySkySat: PropTypes.bool,
  removeResultFromOrder: PropTypes.func,
  removeResultFromView: PropTypes.func,
  results: PropTypes.array.isRequired,
  resultsComplete: PropTypes.bool.isRequired,
  resultsLoading: PropTypes.bool,
  selectMulti: PropTypes.func,
  setActiveResultId: PropTypes.func,
  showDateRangeDialog: PropTypes.func,
  showFiltersPanel: PropTypes.func,
  sortDirection: PropTypes.string,
  subjects: PropTypes.object,
  updateStatus: PropTypes.func,
  viewResults: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
  geometry: state.search.geometry,
  isComparing: state.ui.mode === UI_MODES.COMPARE,
  resultsComplete: state.results.complete,
  sortDirection: state.sidebar.resultsSortDirection,
  subjects: state.compare.subjects,
  onlySkySat: Object.keys(getSelectedItemTypes(state)).every(
    type => type.indexOf('SkySat') >= 0
  ),
  viewResults: state.results.viewResults,
});

const mapDispatchProps = {
  addResultToOrder,
  addResultToView,
  continueSearch,
  notify,
  removeResultFromOrder,
  removeResultFromView,
  selectMulti,
  setActiveResultId,
  updateStatus,
};

export default connect(mapStateToProps, mapDispatchProps)(ResultsList);
